"use client";
import * as React from "react";
import ReactMapGL, {
  Layer,
  LineLayer,
  MapRef,
  Marker,
  NavigationControl,
  Source,
} from "react-map-gl";
import Image from "next/image";
import type { FeatureCollection } from "geojson";

import { IMapBoxMapProps } from "@/interfaces/MapboxMap/MapboxMap";

import vars from "@/config/vars";

//ROUTES STYLE
const lineStyle: LineLayer = {
  type: "line",
  id: "roadLayer",
  layout: {
    "line-cap": "round",
    "line-join": "round",
  },
  paint: {
    "line-width": 4,
    "line-color": "#AC893F",
    "line-opacity": 1,
  },
};

const borderLineStyle: LineLayer = {
  type: "line",
  id: "borderLineLayer",
  layout: {
    "line-cap": "round",
    "line-join": "round",
  },
  paint: {
    "line-width": 5,
    "line-color": "#AC893F",
    "line-opacity": 1,
  },
};

type ViewState = {
  longitude: number;
  latitude: number;
  zoom: number;
};

export default function MapboxMap({
  mainMapMarker,
  mainMarkerImageUrl,
  sourceMarkers,
  source,
}: IMapBoxMapProps) {
  const [start, setStart] = React.useState<number[] | null>(null);
  const [viewState, setViewState] = React.useState<ViewState | null>(null);

  const [end, setEnd] = React.useState<number[] | null>(null);
  const [coordinates, setCoordinates] = React.useState([]);

  const mapRef = React.useRef<MapRef | null>(null);

  const mapStyleUrl = `mapbox://styles/${vars.MapBoxStyle}`;
  const { responsiveImage } = mainMarkerImageUrl;
  const getRoute = async () => {
    try {
      if (end && start) {
        const response = await fetch(
          `https://api.mapbox.com/directions/v5/mapbox/walking/${start[0]},${start[1]};${end[0]},${end[1]}?alternatives=true&steps=true&geometries=geojson&access_token=${vars.MapboxKey}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch route data");
        }

        const data = await response.json();
        const coords = data.routes[0].geometry.coordinates;
        setCoordinates(coords);
      }
    } catch (err) {
      console.error("Error fetching route data:", err);
      return [];
    }
  };
  //fetch data
  React.useEffect(() => {
    if (start && end) {
      getRoute().catch(() => {
        return [];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [end, sourceMarkers]);

  //set the start data
  React.useEffect(() => {
    if (mainMapMarker) {
      const longitude = mainMapMarker?.mapCoordinates?.longitude || 9.158784;
      const latitude = mainMapMarker?.mapCoordinates?.latitude || 45.466345;
      setStart([longitude, latitude]);

      setViewState({
        longitude,
        latitude,
        zoom: 13,
      });
    }
  }, [mainMapMarker]);

  React.useEffect(() => {
    if (source && sourceMarkers?.length) {
      const endPoints = sourceMarkers.find((value) => value.id === source);
      if (endPoints && start) {
        const longitude: number = endPoints?.location?.mapCoordinates?.longitude;
        const latitude = endPoints?.location?.mapCoordinates?.latitude;
        setEnd([longitude, latitude]);
        const center: [number, number] = [(start[0] + longitude) / 2, (start[1] + latitude) / 2];
        const distance = haversine(start[1], start[0], longitude, latitude);
        const zoom = calculateZoom(distance);
        mapRef.current?.flyTo({ center, zoom, duration: 2000 });
      }
    }
  }, [source, sourceMarkers, start]);

  function haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
    const radians = (value: number) => (value * Math.PI) / 180;
    const R = 6371;
    const dLat = radians(lat2 - lat1);
    const dLon = radians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function calculateZoom(distance: number) {
    const thresholdDistance = 10000; // Adjust this threshold as needed
    const maxZoom = 18; // Maximum allowed zoom level
    const minZoom = 5;

    const zoom = Math.min(maxZoom, minZoom + ((maxZoom - minZoom) * distance) / thresholdDistance);
    return zoom;
  }

  const geojson: FeatureCollection = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "LineString", coordinates: [...coordinates] },
        properties: {
          description: "The capital city of France",
        },
      },
    ],
  };

  //markers render
  const markerRender = React.useMemo(() => {
    return (
      sourceMarkers &&
      sourceMarkers.map((sourceMark) => {
        return (
          <Marker
            key={sourceMark.id}
            color="#AC893F"
            longitude={sourceMark?.location?.mapCoordinates?.longitude}
            latitude={sourceMark?.location?.mapCoordinates?.latitude}
          />
        );
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMarkers]);

  return (
    <>
      {sourceMarkers?.length && viewState ? (
        <ReactMapGL
          ref={(map) => {
            mapRef.current = map;
          }}
          mapboxAccessToken={vars.MapboxKey}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyleUrl}
          attributionControl={false}
        >
          {markerRender}
          <NavigationControl position="bottom-right" />
          {end && (
            <Source id="routesSource" type="geojson" data={geojson}>
              <Layer {...borderLineStyle} />
              <Layer {...lineStyle} />
            </Source>
          )}
          {start && (
            <Marker longitude={start[0]} latitude={start[1]}>
              <Image
                src={responsiveImage?.src}
                width={67}
                height={90}
                alt={responsiveImage?.alt || "Marker"}
                className="relative bottom-11"
              />
            </Marker>
          )}
          {end && <Marker longitude={end[0]} latitude={end[1]} color="#AC893F" />}
        </ReactMapGL>
      ) : (
        viewState && (
          <ReactMapGL
            mapboxAccessToken={vars.MapboxKey}
            longitude={viewState.longitude}
            latitude={viewState.latitude}
            zoom={viewState.zoom}
            onMove={(evt) => setViewState(evt.viewState)}
            style={{ width: "100%", height: "100%" }}
            mapStyle={mapStyleUrl}
            attributionControl={false}
          >
            {start && (
              <Marker longitude={start[0]} latitude={start[1]} color="#AC893F">
                <Image
                  src={responsiveImage.src}
                  width={67}
                  height={90}
                  alt={responsiveImage.alt || "Marker"}
                />
              </Marker>
            )}
          </ReactMapGL>
        )
      )}
    </>
  );
}
